import api from '@disco/api/server'
import { getCookie } from '@disco/utils/business-cookie'
import { consentOptions } from '@disco/utils/cookie-consent'
import captureError from '@disco/utils/captureError'

import React, { useEffect, useState } from 'react'
import type { CookieConsentContextType, CookieType } from 'types/cookie-consent'
export const CookieConsentContext: React.Context<CookieConsentContextType> =
  React.createContext<CookieConsentContextType>({
    regionDetected: false,
    bannerRequired: false,
    acceptCookies: () => {},
    consent: {
      necessary: false,
      statistics: false,
    },
  })
type Props = {
  children: React.ReactNode
}
const CONSENT_COOKIE = 'tracking_consent_optin'
export const CookieConsentProvider = ({ children }: Props): React.ReactNode => {
  const [regionDetected, setRegionDetected] = useState(false)
  const [bannerRequired, setBannerRequired] = useState(false)
  const cookie = getCookie(CONSENT_COOKIE)
  let cookieValue = consentOptions.defaultConsent

  try {
    if (cookie) cookieValue = JSON.parse(cookie)
  } catch (e) {
    captureError(e, { extra: { cookie } })
  }

  const [consent, setConsent] = useState(cookieValue)

  const acceptCookies = (data: CookieType) => {
    setConsent(data)
    if (!api.host && window.API_HOST) api.host = window.API_HOST
    return api.acceptCookies(data)
  }

  useEffect(() => {
    if (consent.statistics) {
      return
    }

    const setBanner = async () => {
      if (!api.host && window.API_HOST) api.host = window.API_HOST

      try {
        const result = await api.getGeoIP()

        if (result?.is_eu) {
          setBannerRequired(true)
        }

        setRegionDetected(true)
      } catch (e) {
        captureError(e)
      }
    }

    setBanner()
  }, [consent.statistics])
  const value = {
    regionDetected,
    bannerRequired,
    consent,
    acceptCookies,
  }
  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  )
}
export const useRegionCookieConsentContext = (): CookieConsentContextType => {
  const context = React.useContext(CookieConsentContext)

  if (context === undefined) {
    throw new Error(
      'useRegionCookieConsentContext must be used within a CookieConsentProvider',
    )
  }

  return context
}
